import React from "react";
import styled from "styled-components";
import { textStyles, TextStyles } from "@ui/styles/textStyles";
import { whiteRoundedBlockStyle } from "@ui/styles/containerStyles";
import { Z } from "@ui/styles/zIndex";
import { Card, Section, SectionIntro, SectionTitle } from "./debugUi";

const sample = "Le renard brun saute par-dessus le chien paresseux.";
const fontWeights = [
	{ family: "DMSans", weight: 400, label: "DM Sans Regular" },
	{ family: "DMSans", weight: 600, label: "DM Sans Medium" },
	{ family: "DMSans", weight: 700, label: "DM Sans Bold" },
	{ family: "Sora", weight: 600, label: "Sora Medium" },
	{ family: "Sora", weight: 700, label: "Sora Bold" },
];

const zEntries = Object.entries(Z).filter((entry): entry is [string, number] => typeof entry[1] === "number");

export const TypographySection: React.FC = () => {
	const styleNames = Object.keys(textStyles) as (keyof TextStyles)[];

	return (
		<Section id="typography">
			<SectionTitle>Typographie et styles</SectionTitle>
			<SectionIntro>Polices chargées dans index.html, textStyles du thème, bloc arrondi et z-index.</SectionIntro>

			<Card>
				{fontWeights.map((font) => (
					<FontLine key={font.label} $family={font.family} $weight={font.weight}>
						<span>{font.label}</span>
						{sample}
					</FontLine>
				))}
			</Card>

			<StyleList>
				{styleNames.map((name) => (
					<StyleCard key={name}>
						<StyleName>{name}</StyleName>
						<StyleSample $name={name}>{sample}</StyleSample>
					</StyleCard>
				))}
			</StyleList>

			<Card>
				<StyleName>roundedBlockStyle</StyleName>
				<RoundedDemo>Bloc arrondi du thème</RoundedDemo>
				<StyleName>whiteRoundedBlockStyle</StyleName>
				<WhiteRoundedDemo>Bloc blanc arrondi</WhiteRoundedDemo>
			</Card>

			<Card>
				<StyleName>z-index</StyleName>
				<ZList>
					{zEntries.map(([name, value]) => (
						<li key={name}>
							<code>{name}</code> {value}
						</li>
					))}
					<li>
						<code>above(z)</code> z + 1
					</li>
				</ZList>
			</Card>
		</Section>
	);
};

const FontLine = styled.p<{ $family: string; $weight: number }>`
	margin: 0;
	font-family: ${({ $family }) => $family};
	font-weight: ${({ $weight }) => $weight};
	font-size: 1.05rem;
	color: ${({ theme }) => theme.colors.textPrimary};

	span {
		display: block;
		font-family: DMSans, sans-serif;
		font-weight: 600;
		font-size: 0.75rem;
		color: ${({ theme }) => theme.colors.textSecondary};
		margin-bottom: 2px;
	}
`;

const StyleList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const StyleCard = styled(Card)`
	gap: 6px;
`;

const StyleName = styled.code`
	font-size: 0.75rem;
	font-weight: 700;
	color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyleSample = styled.p<{ $name: keyof TextStyles }>`
	margin: 0;
	${({ theme, $name }) => theme.textStyles[$name]}
`;

const RoundedDemo = styled.div`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 16px;
`;

const WhiteRoundedDemo = styled.div`
	${whiteRoundedBlockStyle};
	padding: 16px;
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
`;

const ZList = styled.ul`
	margin: 0;
	padding-left: 18px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	color: ${({ theme }) => theme.colors.textPrimary};
	font-size: 0.9rem;
`;
